import {LocalStorageSettings, SETTINGS_STORAGE_KEY} from './local-storage.settings';

/**
 * MAP-1583 (review round 2): every "do not show this again" / "do not ask me
 * again" opt-out in the app, listed in ONE place so resetting the settings
 * clears all of them and a new one cannot be forgotten.
 *
 * They come in two shapes:
 * - flags inside the `app-settings` blob (the AI bubbles' checkboxes),
 * - standalone localStorage keys (the apply-model confirmation).
 *
 * Deliberately NOT included: the one-shot onboarding markers
 * `maporium_first_signin_tour_shown` / `maporium_smaplet_tour_shown` and the
 * per-smap `maporium_legend_seen_*` flags. Those record "this was shown once",
 * they are not opt-outs the user ticked, and clearing them would replay the
 * FTUE tour and re-pop the legend panel straight after a settings reset.
 */

/** "Do not show this again" on the experimental-image warning bubble. */
export const IMAGE_WARNING_DISMISSED_FLAG = 'imageGenerationWarningDismissed';

/** "Display on long press only" on the property-generation bubble. */
export const AI_LONG_PRESS_ONLY_FLAG = 'aiGenerationLongPressOnly';

/** "Do not ask again" on the apply-model confirmation dialog. */
export const SKIP_MODEL_APPLICATION_CONFIRMATION_KEY = 'skipModelApplicationConfirmation';

export const DISMISSIBLE_SETTINGS_FLAGS: (keyof LocalStorageSettings)[] = [
  IMAGE_WARNING_DISMISSED_FLAG,
  AI_LONG_PRESS_ONLY_FLAG,
];

export const DISMISSIBLE_STANDALONE_KEYS: string[] = [
  SKIP_MODEL_APPLICATION_CONFIRMATION_KEY,
];

/**
 * Clears every dismissal preference. Safe to call when the whole
 * `app-settings` blob has already been dropped (the blob branch is then a
 * no-op) so the caller does not have to care about ordering.
 */
export function clearDismissiblePreferences(storage: Storage = localStorage): void {
  for (const key of DISMISSIBLE_STANDALONE_KEYS) {
    try {
      storage.removeItem(key);
    } catch {
      // Ignore availability errors, the preference simply stays set.
    }
  }

  try {
    const raw = storage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return;

    const settings: LocalStorageSettings = JSON.parse(raw);
    let changed = false;
    for (const flag of DISMISSIBLE_SETTINGS_FLAGS) {
      if (flag in settings) {
        delete settings[flag];
        changed = true;
      }
    }
    if (changed) {
      storage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    }
  } catch {
    // Corrupt or unavailable storage, nothing sensible to clear.
  }
}
